import React from 'react';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';

const AGENT_ICON_REGISTRY = new Map();

// Default core icons that are likely to be used across all apps
const DEFAULT_CORE_ICONS = {
  'User': { component: PersonIcon, props: { fontSize: "small" } },
  'Assistant': { component: SmartToyIcon, props: { fontSize: "small" } },
  'default': { component: SmartToyIcon, props: { fontSize: "small" } },
};

Object.entries(DEFAULT_CORE_ICONS).forEach(([agentName, config]) => {
  AGENT_ICON_REGISTRY.set(agentName, config);
});

/**
 * Registers a custom icon component for a specific agent name.
 * This allows an implementing app to define icons for its domain-specific agents.
 * @param {string} agentName - The agent name string (e.g., 'Tutor Agent').
 * @param {React.ComponentType} component - The icon component (e.g., SchoolIcon).
 * @param {object} props - Default props to apply to the icon component.
 */
export const registerAgentIcon = (agentName, component, props = { fontSize: "small" }) => {
  if (agentName && component) {
    AGENT_ICON_REGISTRY.set(agentName, { component, props });
  } else {
    console.error("[AgentIconRegistry] Attempted to register invalid agent icon:", { agentName, component });
  }
};

/**
 * Retrieves the appropriate icon component and props for a given agent name.
 * @param {string} agentName - The name of the agent.
 * @returns {{ component: React.ComponentType, props: object }} The icon component and its props.
 */
export const getAgentIconConfig = (agentName) => {
  return AGENT_ICON_REGISTRY.get(agentName) || AGENT_ICON_REGISTRY.get('default');
};

/**
 * A component wrapper to render the agent icon.
 * @param {object} props - The component props.
 * @param {string} props.agentName - The name of the agent.
 * @param {object} props.sx - Custom styles to apply to the icon.
 * @returns {React.ReactElement|null} The rendered icon component.
 */
export const AgentIcon = ({ agentName, sx = {} }) => {
    const iconConfig = getAgentIconConfig(agentName);
    if (!iconConfig || !iconConfig.component) {
        return null;
    }
    const IconComponent = iconConfig.component;
    const finalProps = { ...iconConfig.props, sx };
    return <IconComponent {...finalProps} />;
};